import type { AppMetadata, Permission } from 'paintscout'
import { getHighestRole } from '../../../users/getHighestRole'

// !! PERMISSIONS MUST BE KEPT IN SYNC WITH UPDATE APP METADATA AND ADD TO TOKEN PERMISSIONS !!
// Can copy this function in and remove the types into the file^

/**
 * The custom permissions available to each new-role user type.
 * Used by PermissionsDialog (UI) and DevPermissionsWidget (dev tooling) as the single source of truth.
 */
export const CUSTOM_PERMISSIONS_BY_ROLE: Record<'core' | 'team' | 'painter', Permission[]> = {
  core: [
    'read:all-documents',
    'write:all-documents',
    'write:account-settings',
    'write:account-settings-billing',
    'write:account-settings-user-management',
    'read:reports',
    'export:data'
  ],
  team: [
    'read:dashboard',
    'read:reports',
    'export:data',
    'write:account-settings',
    'write:account-settings-billing',
    'write:account-settings-user-management'
  ],
  painter: [
    'view:client-info',
    'view:job-value',
    'accept-payment:assigned-documents',
    'additional-work:assigned-quotes',
    'contribute:assigned-deals'
  ]
}

export const READ_ALL_DOCUMENTS_PERMISSIONS: Permission[] = [
  'read:all-quotes',
  'read:all-invoices',
  'read:all-deals',
  'read:all-tasks',
  'read:all-events',
  'read:all-contacts',
  'read:all-companies',
  'read:estimator-select'
]

export const WRITE_ALL_DOCUMENTS_PERMISSIONS: Permission[] = [
  'write:all-quotes',
  'send:all-quotes',
  'accept:all-quotes',
  'delete:all-quotes',
  'additional-work:all-quotes',
  'write-presentation-section:all-quotes',
  'write:all-invoices',
  'send:all-invoices',
  'write:all-deals',
  'write:all-tasks',
  'write:all-events',
  'write:all-contacts',
  'write:all-companies',
  'assign:all-documents',
  'accept-payment:all-documents'
]

const OWNER_PERMISSIONS: Permission[] = [
  'read:all-documents',
  'write:all-documents',
  'write:account-settings',
  'write:account-settings-billing',
  'write:account-settings-user-management',
  'read:reports',
  'export:data'
]

const CORE_BASE_PERMISSIONS: Permission[] = [
  'create:quotes',
  'read:assigned-quotes',
  'write:assigned-quotes',
  'send:assigned-quotes',
  'accept:assigned-quotes',
  'delete:assigned-quotes',
  'additional-work:assigned-quotes',
  'write-presentation-section:assigned-quotes',

  'create:contacts',
  'read:all-contacts',
  'write:all-contacts',

  'create:companies',
  'read:all-companies',
  'write:all-companies',

  'create:deals',
  'read:assigned-deals',
  'write:assigned-deals',

  'create:tasks',
  'read:assigned-tasks',
  'write:assigned-tasks',

  'create:events',

  'create:invoices',
  'read:assigned-invoices',
  'write:assigned-invoices',
  'send:assigned-invoices',

  'assign:assigned-documents',
  'accept-payment:assigned-documents',

  'read:dashboard'
]

const TEAM_BASE_PERMISSIONS: Permission[] = [
  'read:all-documents',

  'send:all-quotes',
  'send:all-invoices',
  'additional-work:all-quotes',
  'write-presentation-section:all-quotes',
  'accept-payment:assigned-documents',

  'create:contacts',
  'write:all-contacts',

  'create:companies',
  'write:all-companies',

  'create:deals',
  'write:all-deals',

  'create:tasks',
  'write:all-tasks',

  'create:events',
  'write:all-events',

  'read:estimator-select'
]

const PAINTER_BASE_PERMISSIONS: Permission[] = ['read:assigned-tasks', 'read:assigned-events']

function expandAllDocuments(perms: Permission[]): Permission[] {
  const result = [...perms]
  if (result.includes('read:all-documents')) {
    for (const p of READ_ALL_DOCUMENTS_PERMISSIONS) {
      if (!result.includes(p)) result.push(p)
    }
  }
  if (result.includes('write:all-documents')) {
    for (const p of WRITE_ALL_DOCUMENTS_PERMISSIONS) {
      if (!result.includes(p)) result.push(p)
    }
  }
  return result
}

/**
 * Ensures write:account-settings is present if billing or user-management sub-permissions are requested.
 */
function withAccountSettingsPrerequisite(permissions: Permission[]): Permission[] {
  const needsBase =
    permissions.includes('write:account-settings-billing') ||
    permissions.includes('write:account-settings-user-management')

  if (needsBase && !permissions.includes('write:account-settings')) {
    return ['write:account-settings', ...permissions]
  }

  return permissions
}

export function getPermissionsNext(roles: string[], customPermissions: Permission[], isOwner?: boolean): Permission[] {
  const role = getHighestRole(roles)

  // Privileged internal roles — full access, no custom permissions
  if (role === 'superadmin' || role === 'corporate') {
    return expandAllDocuments([
      'read:all-documents',
      'write:all-documents',
      'assign:all-documents',

      'create:quotes',
      'delete:all-quotes',
      'send:all-quotes',
      'accept:all-quotes',
      'additional-work:all-quotes',

      'create:contacts',

      'create:companies',

      'create:invoices',
      'send:all-invoices',

      'create:deals',

      'create:tasks',

      'create:events',

      'write-presentation-section:all-quotes',

      'write:account-settings',
      'write:account-settings-billing',
      'write:account-settings-user-management',
      'accept-payment:all-documents',

      'read:reports',
      'read:dashboard',
      'export:data',
      'read:estimator-select'
    ])
  }

  if (role === 'support') {
    return expandAllDocuments([
      'read:all-documents',
      'read:estimator-select',
      'send:all-quotes',
      'send:all-invoices',
      'additional-work:all-quotes',
      'accept-payment:assigned-documents'
    ])
  }

  let basePermissions: Permission[]

  if (role === 'core') {
    basePermissions = CORE_BASE_PERMISSIONS
  } else if (role === 'team') {
    basePermissions = TEAM_BASE_PERMISSIONS
  } else if (role === 'painter') {
    basePermissions = PAINTER_BASE_PERMISSIONS
  } else {
    return []
  }

  const validatedCustom = withAccountSettingsPrerequisite(customPermissions)

  // Merge base + custom, then apply owner permissions, deduplicating throughout
  const merged = [...basePermissions]

  for (const perm of validatedCustom) {
    if (!merged.includes(perm)) {
      merged.push(perm)
    }
  }

  if (isOwner) {
    for (const perm of OWNER_PERMISSIONS) {
      if (!merged.includes(perm)) {
        merged.push(perm)
      }
    }
  }

  return expandAllDocuments(merged)
}

const NEW_ROLES = ['core', 'team', 'painter']

/**
 * Resolves the effective permission set for a user from their app_metadata.
 * - New-role users (core/team/painter): computed from roles + customPermissions + isOwner.
 * - Old-role users: reads the pre-stamped permissions array from the JWT.
 */
export function resolvePermissions(appMetadata: AppMetadata): Permission[] {
  const roles = appMetadata?.roles ?? []

  if (roles.some((r) => NEW_ROLES.includes(r))) {
    return getPermissionsNext(roles, appMetadata?.customPermissions ?? [], appMetadata?.isOwner ?? false)
  }

  return appMetadata?.permissions ?? []
}
